<template>
	<div class="products-matrix">
		<AppTable
			ref="orders-table"
			:table-headings="tableHeadings"
			:title="getString('scanned_products')"
			:total-items="tableData.length"
			:items="currentShownData"
			:filters="[]"
			:main-color="'#0752A3'"
			@changed-page="v=>currentPage = v"
			@changed-page-size="v=>pageSize = v"
			allow-download
			@export="exportMatrix"
		/>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import {useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import API from "@/shared/helpers/API";
import {formatDateOnly, formatDateTimeWithSeconds, getLocalDateFromServer} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import menuHeaderAddress from "@/new-menu/components/MenuHeaderAddress.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const menuStore = useMenuStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		return {
			getString: languageStore.getString,
			userStore,
			menuStore,
			scriptStore,
			showNotification: notificationStore.showNotification,
		}
	},
	name: "ProductsMatrix",
	components: {AppTable},
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		}
	},
	data() {
		return {
			pageSize: 10,
			currentPage: 1,
			productsUsers: [],
		}
	},
	computed: {
		tableHeadings() {
			let result = {};

			this.productsUsers.forEach(item => {
				if(!result.hasOwnProperty(item.user_id)) {
					result[item.user_id] = {
						text: item.user_name,
						fieldName: 'user_' + item.user_id,
						sortable: false,
						cols: 1,
					};
				}
			});

			return [{
				text: this.getString('product'),
				fieldName: 'name',
				sortable: false,
				cols: 1,
			}].concat(Object.values(result));
		},
		tableData() {
			let result = {};

			this.productsUsers.forEach(item => {
				if(!result.hasOwnProperty(item.item_id)) {
					result[item.item_id] = {
						name: item.name,
					};
				}
				let key = "user_"+item.user_id;
				if(!result[item.item_id].hasOwnProperty(key)) {
					result[item.item_id][key] = 0;
				}
				result[item.item_id][key] += item.quantity;
			});

			return Object.values(result);
		},
		currentShownData() {
			return this.tableData.slice(
				(this.currentPage - 1) * this.pageSize,
				this.currentPage * this.pageSize,
			);
		},
	},
	methods: {
		exportMatrix(expExtension) {
			let columns = this.tableHeadings.map(heading => {
				if (heading.fieldName === "name") {
					return { header: heading.fieldName ?? '', key: 'name' };
				}
				return {
					header: heading.text + ' (' + heading.fieldName + ')',
					value: c => c[heading.fieldName] ? c[heading.fieldName] : 0,
					type: 'number',
				};
			});

			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm+"_"+this.getString("products_users").toLowerCase()+"_"+this.getString("from").toLowerCase()+"_"+startDate+"_"+this.getString("to").toLowerCase()+"_"+endDate;
			exportToFile({
				columns,
				rows: this.tableData,
				fileName,
				sheetName: this.getString("products_users"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		},
		downloadData() {

			let startDate = this.dates.start;
			let endDate = this.dates.end;

			if(startDate) {
				let endDateDB = formatDateTimeWithSeconds(endDate);
				let otherStartDateDB = formatDateTimeWithSeconds(startDate);

				API.init().getProductsUsersScan(this.userStore.currBusinessId, otherStartDateDB, endDateDB, r => {
					if(r.result && r.result === "OK") {
						this.productsUsers = this.normalizeProductsUsers(r.data);
					}
				});

			}

		},
		normalizeProductsUsers(data) {

			let result = [];
			data.forEach(item => {
				item.date = getLocalDateFromServer(item.date);
				item.quantity = Number(item.quantity);

				if(this.menuStore.items.hasOwnProperty(item.item_id)) {
					item.name = this.menuStore.items[item.item_id].name;
				} else {
					item.name = "Unknown #" + item.item_id;
				}

				item.user_id = Number(item.user_id);
				if(this.userStore.businessUsers && this.userStore.businessUsers.hasOwnProperty(item.user_id)) {
					item.user_name = this.userStore.businessUsers[item.user_id].name;
				} else {
					item.user_name = "Unknown #" + item.user_id;
				}

				result.push(item);
			});
			return result;
		}
	},
	mounted() {

		if(!this.userStore.businessUsers || !Object.keys(this.userStore.businessUsers).length) {
			this.userStore.getBusinessUsers(() => {
				this.downloadData();
			});
		} else {
			this.downloadData();
		}
	}
}
</script>

<style scoped lang="scss">

</style>